import React, { useState, useRef, useEffect, useCallback } from 'react';
import './SearchableSelect.css';

/**
 * SearchableSelect - 部分一致検索付きセレクトボックス
 *
 * Props:
 *   options   - { value: string, label: string }[] 選択肢の配列
 *   value     - string 現在の選択値（option.value）
 *   onChange  - (value: string) => void 選択変更時のコールバック
 *   placeholder - string プレースホルダー（デフォルト: "選択してください"）
 *   disabled  - boolean 無効化フラグ
 *   id        - string フォーム要素のID
 */
function SearchableSelect({
    options = [],
    value,
    onChange,
    placeholder = '選択してください',
    disabled = false,
    id,
    noResultsText = '該当する項目がありません',
    searchPlaceholder = '検索...',
    scrollOnOpen = 'nearest', // 'nearest' | 'center'
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // 選択中のオプションのラベルを取得
    const selectedOption = options.find(opt => opt.value === value);

    // 部分一致フィルタリング（ひらがな・カタカナ・大小文字を区別しない）
    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchText.toLowerCase())
    );

    const isJustOpened = useRef(false);

    // ドロップダウンを開く
    const handleOpen = useCallback(() => {
        if (disabled) return;
        setIsOpen(true);
        setSearchText('');
        const initialIndex = options.findIndex(opt => opt.value === value);
        setHighlightIndex(initialIndex >= 0 ? initialIndex : -1);
        isJustOpened.current = true;
        
        // 次のレンダリングフレームでinputにフォーカス
        setTimeout(() => inputRef.current?.focus(), 0);
    }, [disabled, options, value]);

    // ドロップダウンを閉じる
    const handleClose = useCallback(() => {
        setIsOpen(false);
        setSearchText('');
        setHighlightIndex(-1);
    }, []);

    // オプション選択
    const handleSelect = useCallback((optionValue) => {
        onChange(optionValue);
        handleClose();
    }, [onChange, handleClose]);

    // 選択クリア
    const handleClear = useCallback((e) => {
        e.stopPropagation();
        onChange('');
    }, [onChange]);

    // キーボード操作
    const handleKeyDown = useCallback((e) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                handleOpen();
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightIndex(prev => prev > 0 ? prev - 1 : 0);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightIndex >= 0 && highlightIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightIndex].value);
                }
                break;
            case 'Escape':
                e.preventDefault();
                handleClose();
                break;
            default:
                break;
        }
    }, [isOpen, handleOpen, handleClose, handleSelect, filteredOptions, highlightIndex]);

    // ハイライトされた項目を自動スクロール
    useEffect(() => {
        if (highlightIndex >= 0 && listRef.current) {
            const items = listRef.current.children;
            if (items[highlightIndex]) {
                items[highlightIndex].scrollIntoView({ 
                    block: isJustOpened.current ? scrollOnOpen : 'nearest' 
                });
                isJustOpened.current = false;
            }
        }
    }, [highlightIndex, scrollOnOpen]);

    // 外側クリックでドロップダウンを閉じる
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, handleClose]);

    return (
        <div
            className={`searchable-select ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
            ref={containerRef}
            id={id}
        >
            {/* 選択表示エリア（クリックで開く） */}
            <div
                className="searchable-select-trigger"
                onClick={handleOpen}
                onKeyDown={handleKeyDown}
                tabIndex={disabled ? -1 : 0}
                role="combobox"
                aria-expanded={isOpen}
                aria-controls={id ? `${id}-listbox` : undefined}
                aria-haspopup="listbox"
            >
                {selectedOption ? (
                    <span className="searchable-select-value">{selectedOption.label}</span>
                ) : (
                    <span className="searchable-select-placeholder">{placeholder}</span>
                )}
                <div className="searchable-select-actions">
                    {selectedOption && !disabled && (
                        <button
                            type="button"
                            className="searchable-select-clear"
                            onClick={handleClear}
                            tabIndex={-1}
                            aria-label="選択をクリア"
                        >
                            ×
                        </button>
                    )}
                    <span className="searchable-select-arrow">▾</span>
                </div>
            </div>

            {/* ドロップダウン */}
            {isOpen && (
                <div className="searchable-select-dropdown">
                    <div className="searchable-select-search-wrapper">
                        <input
                            ref={inputRef}
                            type="text"
                            className="searchable-select-search"
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                setHighlightIndex(-1);
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder={searchPlaceholder}
                            autoComplete="off"
                        />
                    </div>
                    <ul
                        className="searchable-select-options"
                        ref={listRef}
                        role="listbox"
                    >
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt, index) => (
                                <li
                                    key={opt.value}
                                    className={`searchable-select-option ${
                                        opt.value === value ? 'selected' : ''
                                    } ${index === highlightIndex ? 'highlighted' : ''}`}
                                    onClick={() => handleSelect(opt.value)}
                                    role="option"
                                    aria-selected={opt.value === value}
                                >
                                    {opt.label}
                                    {opt.value === value && (
                                        <span className="searchable-select-check">✓</span>
                                    )}
                                </li>
                            ))
                        ) : (
                            <li className="searchable-select-no-results">
                                {noResultsText}
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default SearchableSelect;
